import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from "react";
import styled from "styled-components";
import classNames from "classnames";

import useAddressSuggestions from "hooks/useAddressSuggestions";
import FormField from "components/FormField/FormField";
import UIList from "components/UI/List";

const StyledList = styled(UIList)`
  position: absolute;
  box-shadow: 0 0 20px 0 rgba(109, 120, 129, 0.16);
  left: 0;
  top: 77px;
  outline: none;
  width: 100%;
  z-index: 3;

  .list__item {
    cursor: pointer;
  }
  .google-logo {
    cursor: initial;
    height: 18px;
    justify-content: center;
  }
  .google-logo:hover,
  .google-logo.active {
    background: initial;
    color: ${props => props.theme.colors.mako};
  }
`;

const googleLogoListItem = (
  <UIList.Item key="powered_by_google" className="google-logo">
    <img
      src="/assets/images/powered_by_google.png"
      alt="Powered by Google logo"
    />
  </UIList.Item>
);

const AddressField = ({
  id,
  value,
  onChange,
  stateName,
  onAddressComponentChange,
  veReport,
  processNumber,
  ...rest
}) => {
  const placeService = useMemo(
    () =>
      new window.google.maps.places.PlacesService(
        document.createElement("div")
      ),
    []
  );

  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [sessionToken, setSessionToken] = useState(null);
  const [addressSuggestions] = useAddressSuggestions(
    value,
    stateName,
    sessionToken
  );

  useEffect(() => {
    const validValue = value && value.trim();
    if (isFocused && !sessionToken && validValue.length >= 3) {
      setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
    }    
  }, [value, sessionToken, isFocused]);

  const onAddressClick = ({ placeId }) => {
    placeService.getDetails(
      {
        placeId,
        fields: ["formatted_address", "address_component"],
        sessionToken
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          onChange(place.formatted_address);

          const postalCodeComponent = place.address_components.find(
            x => x.types[0] === "postal_code"
          );
          const zipCode = postalCodeComponent
            ? postalCodeComponent.short_name
                : "";

            if(!veReport || !processNumber){
              onAddressComponentChange(id=== "cAddress" ? "cRegZip" : "cACVRegZip", zipCode);
            }            
        }
      }
    );
    setSessionToken(null);
    setIsFocused(false);
  };

  const handleKeyUp = event => {
    switch (event.key) {
      case "Escape":
        setIsFocused(false);
        break;
      case "ArrowUp":
        setHighlightedIndex(h => Math.max(0, h - 1));
        break;
      case "ArrowDown":
        setHighlightedIndex(h =>
          Math.min(addressSuggestions.length - 1, h + 1)
        );
        break;
      case "Enter":
        onAddressClick(addressSuggestions[highlightedIndex]);
        break;
      default:
        break;
    }
  };
  const handleBlur = useCallback(event => {
    if (!containerRef.current.contains(event.relatedTarget)) {
      setIsFocused(false);
    }
  }, []);
  const handleFocus = useCallback(() => setIsFocused(true), []);
  const containerRef = useRef(null);

  const addressItems = addressSuggestions.map((suggestion, i) => (
    <UIList.Item
      key={suggestion.text}
      onClick={() => onAddressClick(suggestion)}
      className={classNames({ active: highlightedIndex === i })}
    >
      {suggestion.text}
    </UIList.Item>
  ));
  addressItems.length && addressItems.push(googleLogoListItem);

  return (   
    <div
      style={{ position: "relative", display: "inherit" }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      ref={containerRef}
    >    
       <FormField
          fieldType="Text"        
          value={value}
          onChange={onChange}
          onKeyUp={handleKeyUp}
          {...rest}
        />
      {isFocused && addressSuggestions.length ? (
        <StyledList tabIndex={0}>{addressItems}</StyledList>
      ) : null}
    </div>
  );
};

export default AddressField;
